import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { DepositMethodId } from '@starsoft/common/enums';
import { parseToSubunits } from '@starsoft/common/utils';
import { useCallback } from 'react';
import useDepositSearchParams from '../../../../hooks/deposit/useDepositSearchParams';
import { useCreateDeposit } from '../useDeposits';

/**
 * Custom hook to handle deposit operations based on the deposit method.
 *
 * This hook provides a `deposit` function that determines the appropriate deposit
 * method to use based on the `depositMethodId` and executes the corresponding
 * deposit function. It also provides a loading state indicating if any deposit
 * operation is currently in progress.
 *
 * @returns {Object} - Returns an object containing the deposit function and loading state.
 * @returns {Function} return.deposit - Function to initiate a deposit operation.
 * @returns {boolean} return.isLoading - Indicates if a deposit operation is currently loading.
 */
export default function useDeposit() {
  // Retrieve the deposit method ID from the search parameters
  const { depositMethodId } = useDepositSearchParams();

  // Initialize deposit functions and loading states for different deposit methods
  const { createDeposit, isLoading: loadingCreateDeposit } = useCreateDeposit();

  /**
   * Function to handle deposit operations based on the deposit method ID.
   *
   * @param {CreateDepositRequestDto} data - The deposit data including amount and coin type.
   */
  const deposit = useCallback(
    (data: CreateDepositRequestDto) => {
      switch (depositMethodId?.toString()) {
        case DepositMethodId.Crypto?.toString():
          // No operation for Crypto deposit method
          break;
        default:
          createDeposit({
            amount: parseToSubunits(data?.amount, data?.coin),
            methodId: depositMethodId,
            coin: undefined,
          });
      }
    },
    [depositMethodId, createDeposit],
  );

  // Determine if any deposit operation is currently loading
  const isLoading = loadingCreateDeposit;

  // Return the deposit function and loading state
  return {
    deposit,
    isLoading,
  };
}
